/** Route gates (spec §8.3, §10.6). A password-only session or a pending next step never reaches a data screen. */
import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { Skeleton } from '@/components/ui/misc'
import { ScreenError } from './Forbidden'
import { type NextStep, returnPath, stepPath, useSession } from '@/lib/auth'
import { canAll } from '@/lib/permissions'
import { t } from '@/lib/text'
import { Forbidden } from './Forbidden'
import { routeForPath } from './routes'

function Loading() {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-live="polite">
      <span className="sr-only">{t('core.shell.loading')}</span>
      <Skeleton className="h-8 w-48" />
    </div>
  )
}

/** Logged-in area. Anonymous → Login; pre_auth or a pending next step → that step. */
export function RequireUser() {
  const { data: session, isError, refetch } = useSession()
  const location = useLocation()
  // The server could not be reached (and no session was loaded before): say so instead of loading forever.
  if (!session && isError) return <ScreenError onRetry={() => void refetch()} />
  if (!session) return <Loading />
  if (session.state === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  if (session.state === 'pre_auth') return <Navigate to={stepPath(session.nextStep)} replace />
  if (session.me.next_step) return <Navigate to={stepPath(session.me.next_step)} replace />
  return <Outlet />
}

/** Inside the shell: the screen's permission keys from the route registry. */
export function RequirePermission({ children }: { children: ReactNode }) {
  const { data: session } = useSession()
  const location = useLocation()
  const route = routeForPath(location.pathname)
  const perms = session?.state === 'ready' ? session.me.permissions : undefined
  if (route && !canAll(perms, route.permissions)) return <Forbidden />
  return children
}

/** Login, forgot and reset: a logged-in user goes Home, a mid-login user to their step. */
export function AnonymousOnly() {
  const { data: session } = useSession()
  if (!session) return <Loading />
  if (session.state === 'ready') return <Navigate to={session.me.next_step ? stepPath(session.me.next_step) : returnPath()} replace />
  if (session.state === 'pre_auth') return <Navigate to={stepPath(session.nextStep)} replace />
  return <Outlet />
}

/** A step screen (/login/2fa, /enrol-2fa, /change-password): only for the session whose next step it is,
 * plus logged-in users for the screens that also serve them (enrol from My details, change password). */
export function RequireStep({ step, allowReady = false }: { step: NextStep; allowReady?: boolean }) {
  const { data: session } = useSession()
  if (!session) return <Loading />
  if (session.state === 'anonymous') return <Navigate to="/login" replace />
  if (session.state === 'pre_auth') {
    return session.nextStep === step ? <Outlet /> : <Navigate to={stepPath(session.nextStep)} replace />
  }
  const pending = session.me.next_step
  if (pending === step || (allowReady && !pending)) return <Outlet />
  return <Navigate to={pending ? stepPath(pending) : returnPath()} replace />
}
